/* eslint-disable */
import Long from "long";
import _m0 from "protobufjs/minimal";
import { UiButton } from "../../gen/skills/ui_button";
import { UiPrompt } from "../../gen/skills/ui_prompt";
import { UiRadioGroup } from "../../gen/skills/ui_radio_group";
import { UiSlider } from "../../gen/skills/ui_slider";
import { UiToggle } from "../../gen/skills/ui_toggle";

export const protobufPackage = "skills";

/** Future proofing designs by anticipating that there is going to be a notion of "erroring" */
export enum AttributeStyle {
  /**
   * INVALID - Invalid is the default protobuf uninitialized enum value. It will default to NORMAL platform
   * side
   */
  INVALID = 0,
  /** NORMAL - This is viewed as white text, the default in the workflow UI. */
  NORMAL = 1,
  /** WARNING - This is viewed as golden text, used to warn users about certain actions. */
  WARNING = 2,
  /** DANGER - This is viewed as red text, used to denote errors in the workflow. */
  DANGER = 3,
  UNRECOGNIZED = -1,
}

export function attributeStyleFromJSON(object: any): AttributeStyle {
  switch (object) {
    case 0:
    case "INVALID":
      return AttributeStyle.INVALID;
    case 1:
    case "NORMAL":
      return AttributeStyle.NORMAL;
    case 2:
    case "WARNING":
      return AttributeStyle.WARNING;
    case 3:
    case "DANGER":
      return AttributeStyle.DANGER;
    case -1:
    case "UNRECOGNIZED":
    default:
      return AttributeStyle.UNRECOGNIZED;
  }
}

export function attributeStyleToJSON(object: AttributeStyle): string {
  switch (object) {
    case AttributeStyle.INVALID:
      return "INVALID";
    case AttributeStyle.NORMAL:
      return "NORMAL";
    case AttributeStyle.WARNING:
      return "WARNING";
    case AttributeStyle.DANGER:
      return "DANGER";
    case AttributeStyle.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/** The icon type we are using in a UiBasicScreen */
export enum WorkflowIconType {
  UNSPECIFIED = 0,
  STATIC_IMAGE = 1,
  LOTTIE_ANIMATION = 2,
  UNRECOGNIZED = -1,
}

export function workflowIconTypeFromJSON(object: any): WorkflowIconType {
  switch (object) {
    case 0:
    case "UNSPECIFIED":
      return WorkflowIconType.UNSPECIFIED;
    case 1:
    case "STATIC_IMAGE":
      return WorkflowIconType.STATIC_IMAGE;
    case 2:
    case "LOTTIE_ANIMATION":
      return WorkflowIconType.LOTTIE_ANIMATION;
    case -1:
    case "UNRECOGNIZED":
    default:
      return WorkflowIconType.UNRECOGNIZED;
  }
}

export function workflowIconTypeToJSON(object: WorkflowIconType): string {
  switch (object) {
    case WorkflowIconType.UNSPECIFIED:
      return "UNSPECIFIED";
    case WorkflowIconType.STATIC_IMAGE:
      return "STATIC_IMAGE";
    case WorkflowIconType.LOTTIE_ANIMATION:
      return "LOTTIE_ANIMATION";
    case WorkflowIconType.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/**
 * Who controls the display state of the menu.
 * Unspecified acts as "indifferent", i.e. menu state responds to both skill & client commands
 * Skill owned blocks user input, and only responds to skill requests.
 * Client owned blocks skill input and only responds to user requests.
 *
 * Skill owned example: During takeoff phase, the menu is forced in minimized mode until takeoff
 * is complete.
 *
 * Client owned example: After all steps are complete in workflow menu, user can open/close menu
 * as they see fit.
 */
export enum WorkflowMenuDisplayPreference {
  PREF_UNSPECIFIED = 0,
  PREF_SKILL_OWNED = 1,
  PREF_CLIENT_OWNED = 2,
  UNRECOGNIZED = -1,
}

export function workflowMenuDisplayPreferenceFromJSON(object: any): WorkflowMenuDisplayPreference {
  switch (object) {
    case 0:
    case "PREF_UNSPECIFIED":
      return WorkflowMenuDisplayPreference.PREF_UNSPECIFIED;
    case 1:
    case "PREF_SKILL_OWNED":
      return WorkflowMenuDisplayPreference.PREF_SKILL_OWNED;
    case 2:
    case "PREF_CLIENT_OWNED":
      return WorkflowMenuDisplayPreference.PREF_CLIENT_OWNED;
    case -1:
    case "UNRECOGNIZED":
    default:
      return WorkflowMenuDisplayPreference.UNRECOGNIZED;
  }
}

export function workflowMenuDisplayPreferenceToJSON(object: WorkflowMenuDisplayPreference): string {
  switch (object) {
    case WorkflowMenuDisplayPreference.PREF_UNSPECIFIED:
      return "PREF_UNSPECIFIED";
    case WorkflowMenuDisplayPreference.PREF_SKILL_OWNED:
      return "PREF_SKILL_OWNED";
    case WorkflowMenuDisplayPreference.PREF_CLIENT_OWNED:
      return "PREF_CLIENT_OWNED";
    case WorkflowMenuDisplayPreference.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

export enum WorkflowMenuDisplayState {
  DISPLAY_UNSPECIFIED = 0,
  DISPLAY_MAXIMIZED = 1,
  DISPLAY_MINIMIZED = 2,
  UNRECOGNIZED = -1,
}

export function workflowMenuDisplayStateFromJSON(object: any): WorkflowMenuDisplayState {
  switch (object) {
    case 0:
    case "DISPLAY_UNSPECIFIED":
      return WorkflowMenuDisplayState.DISPLAY_UNSPECIFIED;
    case 1:
    case "DISPLAY_MAXIMIZED":
      return WorkflowMenuDisplayState.DISPLAY_MAXIMIZED;
    case 2:
    case "DISPLAY_MINIMIZED":
      return WorkflowMenuDisplayState.DISPLAY_MINIMIZED;
    case -1:
    case "UNRECOGNIZED":
    default:
      return WorkflowMenuDisplayState.UNRECOGNIZED;
  }
}

export function workflowMenuDisplayStateToJSON(object: WorkflowMenuDisplayState): string {
  switch (object) {
    case WorkflowMenuDisplayState.DISPLAY_UNSPECIFIED:
      return "DISPLAY_UNSPECIFIED";
    case WorkflowMenuDisplayState.DISPLAY_MAXIMIZED:
      return "DISPLAY_MAXIMIZED";
    case WorkflowMenuDisplayState.DISPLAY_MINIMIZED:
      return "DISPLAY_MINIMIZED";
    case WorkflowMenuDisplayState.UNRECOGNIZED:
    default:
      return "UNRECOGNIZED";
  }
}

/** A simple container for a ui element that will be shown in the workflow side pane */
export interface WorkflowElement {
  slider?: UiSlider | undefined;
  toggle?: UiToggle | undefined;
  button?: UiButton | undefined;
  label?: UiLabel | undefined;
  stats?: UiStatsView | undefined;
  textField?: UiTextField | undefined;
  image?: UiImage | undefined;
  submenu?: UiSubmenu | undefined;
  picker?: UiRadioGroup | undefined;
  space?: UiFlexibleSpace | undefined;
  progressBar?: UiProgressBar | undefined;
  checkbox?: UiCheckbox | undefined;
  imageSegmentControl?: UiImageSegmentControl | undefined;
}

export interface UiImageSegmentControl {
  id: string;
  label: string;
  imageId: string;
  numColumns: number;
  options: UiSegmentControlOption[];
}

export interface UiSegmentControlOption {
  id: string;
  selected: boolean;
}

/**
 * Flexible space acts as a expander in the workflow pane in between elements.
 * This benefits the platform layer by keeping it "dumb" and allowing it to not know the layout
 * of every step.
 *
 * When added to the workflow elements array. The platform layer should push all elements following
 * the flexible space towards the bottom of the workflow pane. Except in cases where the height of
 * the the workflow pane is smaller than the computed distance to the bottom. i.e. the elements
 * can't be pushed to the bottom.
 */
export interface UiFlexibleSpace {
}

export interface UiImage {
  id: string;
  imageId: string;
}

export interface UiLabel {
  id: string;
  /**
   * TODO(bojanin): If the antipated "red text" or inter-textfield design appears in future
   * versions, deprecate the warning_text field and design an api that maps to NSAttributedString
   */
  text: string;
  /**
   * If warning text is set, it gets concatenated with the value field on the mobile side.
   * The warning text color is yellow, the value color following it will be the default.
   */
  warningText: string;
}

export interface UiTextField {
  id: string;
  value: string;
  placeholder: string;
  disabled: boolean;
  isNumerical: boolean;
}

/**
 * Workflow steps are expandable sections that show in the workflow side pane. Workflow steps
 * can contain an array of workflow elements which are laid out in a vertical stack in the
 * workflow side pane.
 */
export interface WorkflowStep {
  stepId: string;
  stepTitle: string;
  completed: boolean;
  isEditable: boolean;
  elements: WorkflowElement[];
}

/** Used to depict a user setting and its current value various settings/flight menus. */
export interface UiStat {
  label: string;
  value: string;
  style: AttributeStyle;
}

export interface UiSubmenu {
  /** Unique id used to subscript the submenu hashmap on client side */
  id: string;
  /** unique id that maps to a WorkflowSettingsPage page_id */
  pageId: string;
  /** left aligned main text label */
  textLabel: string;
  /** right aligned detail label, used to display choices if applicable */
  detailLabel: string;
  /**
   * settings choices that reduce safety or autonomy of the vehicle should display a
   * danger indicator (i.e. Reduced obstacle avoidance)
   */
  showDangerIndicator: boolean;
  /**
   * Signifies whether or not the submenu can be navigated into.
   * i.e. Used for when a menu setting "unlocks" a submenu
   */
  disabled: boolean;
  /**
   * A tag that can be displayed over the item to mark it.
   * As experimental for example.
   */
  tag: string;
}

/** Request sent by skill to change the state of the workflow menu */
export interface WorkflowMenuDisplayStateRequest {
  /** What state the skill would like the menu to be */
  state: WorkflowMenuDisplayState;
  /** preference on owning the display state of the workflow menu */
  preference: WorkflowMenuDisplayPreference;
}

/** Lightweight ack sent by clients to confirm receipt of a WorkflowLayout. */
export interface WorkflowLayoutAck {
  /** utime when published by the client */
  utime: number;
  /** nonce of the WorkflowLayout being acknowledged */
  nonce: number;
}

/**
 * Current Workflow Menu depicted on the phone by the user.
 * NOTE(bojanin): Workflows can have up to 2 open steps at a time
 * * The first being the current step dictated by the skill. This step is permenantly open until
 * completed.
 * * The second being any previous step thats been previously completed.
 */
export interface WorkflowClientState {
  /** Time when published by client */
  utime: number;
  /** nonce published by vehicle in workflow layout */
  nonce: number;
  /**
   * id of the current step, permanently open client side until the current step counter is
   * incremented
   */
  primaryStepId: string;
  /** id of the secondary step if open. Otherwise, the field wont be set. */
  secondaryStepId: string;
  /** last time ui changed (cause by vehicle or client) */
  lastUpdateUtime: number;
  /** List of page ids where the last element is the currently visible page */
  pageStack: string[];
  /** Current workflow menu state that user has chosen. */
  displayState: WorkflowMenuDisplayState;
}

export interface UiStatsView {
  id: string;
  stats: UiStat[];
}

/**
 * WorkflowHUD Holds the workflow related cockpit UI.
 * Examples include: Workflow trigger buttons, toggle pillar mesh buttons, etc
 */
export interface WorkflowHUD {
  /**
   * Array of buttons to show on the bottom half of the cockpit.
   * 1 button is displayed on the right corner
   * 2 buttons are displayed by having 2 side-by-side in the right corner.
   * 3 buttons are displayed by having 1 in the left corner and two side-by-side in the right corner
   * 4 buttons are displayed by having two pairs side-by-side in left and right corner.
   * No current design for 5+ buttons
   */
  hudButtons: UiButton[];
  /** Widget used to depict various statistics in the top right corner of the cockpit */
  statistics:
    | UiStatsView
    | undefined;
  /** Special case buttons, explicitly named and controlled by skills. */
  photoCapture: UiButton | undefined;
  toggleCoverageMesh:
    | UiButton
    | undefined;
  /** Frozen view toggle button, gives birds eye view of the vehicle. */
  viewpointSelection:
    | UiButton
    | undefined;
  /** Skill controlled RTH button. */
  rthButton:
    | UiButton
    | undefined;
  /** Skill controlled button to toggle map display */
  mapToggleButton:
    | UiButton
    | undefined;
  /**
   * C78 only, skill allows hardware buttons to be tapped.
   * C78 pause button sends a ui_action_t message with item_id="hw_pause".
   * C78 home button sends a ui_action_t message with item_id="hw_return".
   */
  enableHwButtons: boolean;
  /** Text displayed on top right corner. */
  prompts: UiPrompt[];
}

/**
 * A Workflow settings page can be pushed onto a "stack" in the workflow layout, allowing
 * A user to navigate a settings tree.
 */
export interface WorkflowSettingsPage {
  pageId: string;
  pageTitle: string;
  elements: WorkflowElement[];
}

/** A Workflow screen is UI that can be shown before and after take off */
export interface WorkflowScreen {
  basic?: UiScreenBasic | undefined;
  postFlight?:
    | UiScreenPostFlight
    | undefined;
  /** Tells the mobile app to exit the cockpit after its complete. */
  exitCockpit: boolean;
}

export interface UiScreenPostFlight {
  /** A unique identifier for this screen */
  id: string;
  /** Title text for the screen, shown at the top of the screen. */
  title: string;
  /** Filepath of the main image to show in the post flight summary of the screen. */
  imagePath: string;
  /** Heading shown to the right of the main image. */
  heading: string;
  /** Subheading shown under the heading. */
  subheading: string;
  /** Post flight statistics to show under the subheading */
  stats:
    | UiStatsView
    | undefined;
  /** Buttons laid out in a row on the bottom of the screen */
  buttons: UiButton[];
  /** If populated, the screen shows a checkbox with the given message */
  checkbox: UiCheckbox | undefined;
}

export interface UiScreenBasic {
  /** A unique identifier for this screen */
  id: string;
  /** image id of an icon that displays above other UI elements. */
  imageId: string;
  /** What type of icon image we're sending over. Used to decide how to render it on the client. */
  iconType: WorkflowIconType;
  /** Title text for the screen */
  title: string;
  /** Subtitle text that shows underneath the title */
  subtitle: string;
  /** Buttons laid out in a row on the bottom of the screen */
  buttons: UiButton[];
  /**
   * If populated, the screen shows a disclaimer check box with the following message.
   * If check box is checked by user, it's persisted vehicle side.
   */
  checkbox:
    | UiCheckbox
    | undefined;
  /**
   * Current progress bar state to show during a arbitrary task performed by vehicle.
   * progress_ratio = 0.0 means do not display.
   * progress_ratio = -1 means indeterminate state.
   * progress (0.0-100.0] will be displayed per usual.
   */
  progressRatio: number;
  /**
   * if populated the screen will look like the gate screens, splitted in left and right pane
   * with the image on the left (usually a vehicle image)
   */
  leftPaneImageId: string;
  /**
   * Scan ID used to open model viewer if applicable.  A value here will cause mobile to show
   * a button to launch the model viewer.
   */
  scanId: string;
}

/**
 * Checkbox meant to be embeddable into UiScreens.
 * Notably, used for "Do not show this again." type of messages (disclaimers).
 */
export interface UiCheckbox {
  id: string;
  text: string;
  value: boolean;
}

/**
 * Progress bar to be shown in workflow
 * The progress_ratio follows the same rules as in UiScreenBasic
 * NOTE (jason.rinn) This should work in a workflow step or settings page.
 */
export interface UiProgressBar {
  id: string;
  progressRatio: number;
}

/** Top level message published over skybus */
export interface WorkflowLayout {
  /** Utime published by vehicle, */
  utime: number;
  /** Nonce to be used by clients to ack the layout. */
  nonce: number;
  /** Title text displayed on the workflow status bar */
  statusTitle: string;
  /** Detail text displayed on the workflow status bar */
  statusDetail: string;
  /** Index of the current step to expand in the workflow status bar */
  currentStep: number;
  /** All workflow steps in the workflow */
  steps: WorkflowStep[];
  /** Current stack of pages to show in the workflow pane */
  pages: WorkflowSettingsPage[];
  screen:
    | WorkflowScreen
    | undefined;
  /** request the client to animate the menu to the given state and preference. */
  request:
    | WorkflowMenuDisplayStateRequest
    | undefined;
  /** skill specific cockpit UI */
  hud: WorkflowHUD | undefined;
  headerAccessoryButton: UiButton | undefined;
}

function createBaseWorkflowElement(): WorkflowElement {
  return {
    slider: undefined,
    toggle: undefined,
    button: undefined,
    label: undefined,
    stats: undefined,
    textField: undefined,
    image: undefined,
    submenu: undefined,
    picker: undefined,
    space: undefined,
    progressBar: undefined,
    checkbox: undefined,
    imageSegmentControl: undefined,
  };
}

export const WorkflowElement = {
  encode(message: WorkflowElement, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.slider !== undefined) {
      UiSlider.encode(message.slider, writer.uint32(10).fork()).ldelim();
    }
    if (message.toggle !== undefined) {
      UiToggle.encode(message.toggle, writer.uint32(18).fork()).ldelim();
    }
    if (message.button !== undefined) {
      UiButton.encode(message.button, writer.uint32(26).fork()).ldelim();
    }
    if (message.label !== undefined) {
      UiLabel.encode(message.label, writer.uint32(34).fork()).ldelim();
    }
    if (message.stats !== undefined) {
      UiStatsView.encode(message.stats, writer.uint32(42).fork()).ldelim();
    }
    if (message.textField !== undefined) {
      UiTextField.encode(message.textField, writer.uint32(50).fork()).ldelim();
    }
    if (message.image !== undefined) {
      UiImage.encode(message.image, writer.uint32(58).fork()).ldelim();
    }
    if (message.submenu !== undefined) {
      UiSubmenu.encode(message.submenu, writer.uint32(66).fork()).ldelim();
    }
    if (message.picker !== undefined) {
      UiRadioGroup.encode(message.picker, writer.uint32(74).fork()).ldelim();
    }
    if (message.space !== undefined) {
      UiFlexibleSpace.encode(message.space, writer.uint32(82).fork()).ldelim();
    }
    if (message.progressBar !== undefined) {
      UiProgressBar.encode(message.progressBar, writer.uint32(90).fork()).ldelim();
    }
    if (message.checkbox !== undefined) {
      UiCheckbox.encode(message.checkbox, writer.uint32(98).fork()).ldelim();
    }
    if (message.imageSegmentControl !== undefined) {
      UiImageSegmentControl.encode(message.imageSegmentControl, writer.uint32(106).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): WorkflowElement {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseWorkflowElement();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.slider = UiSlider.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.toggle = UiToggle.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.button = UiButton.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.label = UiLabel.decode(reader, reader.uint32());
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.stats = UiStatsView.decode(reader, reader.uint32());
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.textField = UiTextField.decode(reader, reader.uint32());
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.image = UiImage.decode(reader, reader.uint32());
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.submenu = UiSubmenu.decode(reader, reader.uint32());
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.picker = UiRadioGroup.decode(reader, reader.uint32());
          continue;
        case 10:
          if (tag !== 82) {
            break;
          }

          message.space = UiFlexibleSpace.decode(reader, reader.uint32());
          continue;
        case 11:
          if (tag !== 90) {
            break;
          }

          message.progressBar = UiProgressBar.decode(reader, reader.uint32());
          continue;
        case 12:
          if (tag !== 98) {
            break;
          }

          message.checkbox = UiCheckbox.decode(reader, reader.uint32());
          continue;
        case 13:
          if (tag !== 106) {
            break;
          }

          message.imageSegmentControl = UiImageSegmentControl.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): WorkflowElement {
    return {
      slider: isSet(object.slider) ? UiSlider.fromJSON(object.slider) : undefined,
      toggle: isSet(object.toggle) ? UiToggle.fromJSON(object.toggle) : undefined,
      button: isSet(object.button) ? UiButton.fromJSON(object.button) : undefined,
      label: isSet(object.label) ? UiLabel.fromJSON(object.label) : undefined,
      stats: isSet(object.stats) ? UiStatsView.fromJSON(object.stats) : undefined,
      textField: isSet(object.textField) ? UiTextField.fromJSON(object.textField) : undefined,
      image: isSet(object.image) ? UiImage.fromJSON(object.image) : undefined,
      submenu: isSet(object.submenu) ? UiSubmenu.fromJSON(object.submenu) : undefined,
      picker: isSet(object.picker) ? UiRadioGroup.fromJSON(object.picker) : undefined,
      space: isSet(object.space) ? UiFlexibleSpace.fromJSON(object.space) : undefined,
      progressBar: isSet(object.progressBar) ? UiProgressBar.fromJSON(object.progressBar) : undefined,
      checkbox: isSet(object.checkbox) ? UiCheckbox.fromJSON(object.checkbox) : undefined,
      imageSegmentControl: isSet(object.imageSegmentControl)
        ? UiImageSegmentControl.fromJSON(object.imageSegmentControl)
        : undefined,
    };
  },

  toJSON(message: WorkflowElement): unknown {
    const obj: any = {};
    if (message.slider !== undefined) {
      obj.slider = UiSlider.toJSON(message.slider);
    }
    if (message.toggle !== undefined) {
      obj.toggle = UiToggle.toJSON(message.toggle);
    }
    if (message.button !== undefined) {
      obj.button = UiButton.toJSON(message.button);
    }
    if (message.label !== undefined) {
      obj.label = UiLabel.toJSON(message.label);
    }
    if (message.stats !== undefined) {
      obj.stats = UiStatsView.toJSON(message.stats);
    }
    if (message.textField !== undefined) {
      obj.textField = UiTextField.toJSON(message.textField);
    }
    if (message.image !== undefined) {
      obj.image = UiImage.toJSON(message.image);
    }
    if (message.submenu !== undefined) {
      obj.submenu = UiSubmenu.toJSON(message.submenu);
    }
    if (message.picker !== undefined) {
      obj.picker = UiRadioGroup.toJSON(message.picker);
    }
    if (message.space !== undefined) {
      obj.space = UiFlexibleSpace.toJSON(message.space);
    }
    if (message.progressBar !== undefined) {
      obj.progressBar = UiProgressBar.toJSON(message.progressBar);
    }
    if (message.checkbox !== undefined) {
      obj.checkbox = UiCheckbox.toJSON(message.checkbox);
    }
    if (message.imageSegmentControl !== undefined) {
      obj.imageSegmentControl = UiImageSegmentControl.toJSON(message.imageSegmentControl);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<WorkflowElement>, I>>(base?: I): WorkflowElement {
    return WorkflowElement.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<WorkflowElement>, I>>(object: I): WorkflowElement {
    const message = createBaseWorkflowElement();
    message.slider = (object.slider !== undefined && object.slider !== null)
      ? UiSlider.fromPartial(object.slider)
      : undefined;
    message.toggle = (object.toggle !== undefined && object.toggle !== null)
      ? UiToggle.fromPartial(object.toggle)
      : undefined;
    message.button = (object.button !== undefined && object.button !== null)
      ? UiButton.fromPartial(object.button)
      : undefined;
    message.label = (object.label !== undefined && object.label !== null)
      ? UiLabel.fromPartial(object.label)
      : undefined;
    message.stats = (object.stats !== undefined && object.stats !== null)
      ? UiStatsView.fromPartial(object.stats)
      : undefined;
    message.textField = (object.textField !== undefined && object.textField !== null)
      ? UiTextField.fromPartial(object.textField)
      : undefined;
    message.image = (object.image !== undefined && object.image !== null)
      ? UiImage.fromPartial(object.image)
      : undefined;
    message.submenu = (object.submenu !== undefined && object.submenu !== null)
      ? UiSubmenu.fromPartial(object.submenu)
      : undefined;
    message.picker = (object.picker !== undefined && object.picker !== null)
      ? UiRadioGroup.fromPartial(object.picker)
      : undefined;
    message.space = (object.space !== undefined && object.space !== null)
      ? UiFlexibleSpace.fromPartial(object.space)
      : undefined;
    message.progressBar = (object.progressBar !== undefined && object.progressBar !== null)
      ? UiProgressBar.fromPartial(object.progressBar)
      : undefined;
    message.checkbox = (object.checkbox !== undefined && object.checkbox !== null)
      ? UiCheckbox.fromPartial(object.checkbox)
      : undefined;
    message.imageSegmentControl = (object.imageSegmentControl !== undefined && object.imageSegmentControl !== null)
      ? UiImageSegmentControl.fromPartial(object.imageSegmentControl)
      : undefined;
    return message;
  },
};

function createBaseUiImageSegmentControl(): UiImageSegmentControl {
  return { id: "", label: "", imageId: "", numColumns: 0, options: [] };
}

export const UiImageSegmentControl = {
  encode(message: UiImageSegmentControl, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.label !== "") {
      writer.uint32(18).string(message.label);
    }
    if (message.imageId !== "") {
      writer.uint32(26).string(message.imageId);
    }
    if (message.numColumns !== 0) {
      writer.uint32(32).int32(message.numColumns);
    }
    for (const v of message.options) {
      UiSegmentControlOption.encode(v!, writer.uint32(42).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UiImageSegmentControl {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUiImageSegmentControl();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.label = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.imageId = reader.string();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.numColumns = reader.int32();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.options.push(UiSegmentControlOption.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UiImageSegmentControl {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      label: isSet(object.label) ? globalThis.String(object.label) : "",
      imageId: isSet(object.imageId) ? globalThis.String(object.imageId) : "",
      numColumns: isSet(object.numColumns) ? globalThis.Number(object.numColumns) : 0,
      options: globalThis.Array.isArray(object?.options)
        ? object.options.map((e: any) => UiSegmentControlOption.fromJSON(e))
        : [],
    };
  },

  toJSON(message: UiImageSegmentControl): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.label !== "") {
      obj.label = message.label;
    }
    if (message.imageId !== "") {
      obj.imageId = message.imageId;
    }
    if (message.numColumns !== 0) {
      obj.numColumns = Math.round(message.numColumns);
    }
    if (message.options?.length) {
      obj.options = message.options.map((e) => UiSegmentControlOption.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UiImageSegmentControl>, I>>(base?: I): UiImageSegmentControl {
    return UiImageSegmentControl.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UiImageSegmentControl>, I>>(object: I): UiImageSegmentControl {
    const message = createBaseUiImageSegmentControl();
    message.id = object.id ?? "";
    message.label = object.label ?? "";
    message.imageId = object.imageId ?? "";
    message.numColumns = object.numColumns ?? 0;
    message.options = object.options?.map((e) => UiSegmentControlOption.fromPartial(e)) || [];
    return message;
  },
};

function createBaseUiSegmentControlOption(): UiSegmentControlOption {
  return { id: "", selected: false };
}

export const UiSegmentControlOption = {
  encode(message: UiSegmentControlOption, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.selected === true) {
      writer.uint32(32).bool(message.selected);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UiSegmentControlOption {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUiSegmentControlOption();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.selected = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UiSegmentControlOption {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      selected: isSet(object.selected) ? globalThis.Boolean(object.selected) : false,
    };
  },

  toJSON(message: UiSegmentControlOption): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.selected === true) {
      obj.selected = message.selected;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UiSegmentControlOption>, I>>(base?: I): UiSegmentControlOption {
    return UiSegmentControlOption.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UiSegmentControlOption>, I>>(object: I): UiSegmentControlOption {
    const message = createBaseUiSegmentControlOption();
    message.id = object.id ?? "";
    message.selected = object.selected ?? false;
    return message;
  },
};

function createBaseUiFlexibleSpace(): UiFlexibleSpace {
  return {};
}

export const UiFlexibleSpace = {
  encode(_: UiFlexibleSpace, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UiFlexibleSpace {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUiFlexibleSpace();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(_: any): UiFlexibleSpace {
    return {};
  },

  toJSON(_: UiFlexibleSpace): unknown {
    const obj: any = {};
    return obj;
  },

  create<I extends Exact<DeepPartial<UiFlexibleSpace>, I>>(base?: I): UiFlexibleSpace {
    return UiFlexibleSpace.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UiFlexibleSpace>, I>>(_: I): UiFlexibleSpace {
    const message = createBaseUiFlexibleSpace();
    return message;
  },
};

function createBaseUiImage(): UiImage {
  return { id: "", imageId: "" };
}

export const UiImage = {
  encode(message: UiImage, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.imageId !== "") {
      writer.uint32(18).string(message.imageId);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UiImage {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUiImage();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.imageId = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UiImage {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      imageId: isSet(object.imageId) ? globalThis.String(object.imageId) : "",
    };
  },

  toJSON(message: UiImage): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.imageId !== "") {
      obj.imageId = message.imageId;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UiImage>, I>>(base?: I): UiImage {
    return UiImage.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UiImage>, I>>(object: I): UiImage {
    const message = createBaseUiImage();
    message.id = object.id ?? "";
    message.imageId = object.imageId ?? "";
    return message;
  },
};

function createBaseUiLabel(): UiLabel {
  return { id: "", text: "", warningText: "" };
}

export const UiLabel = {
  encode(message: UiLabel, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.text !== "") {
      writer.uint32(18).string(message.text);
    }
    if (message.warningText !== "") {
      writer.uint32(26).string(message.warningText);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UiLabel {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUiLabel();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.text = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.warningText = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UiLabel {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      text: isSet(object.text) ? globalThis.String(object.text) : "",
      warningText: isSet(object.warningText) ? globalThis.String(object.warningText) : "",
    };
  },

  toJSON(message: UiLabel): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.text !== "") {
      obj.text = message.text;
    }
    if (message.warningText !== "") {
      obj.warningText = message.warningText;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UiLabel>, I>>(base?: I): UiLabel {
    return UiLabel.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UiLabel>, I>>(object: I): UiLabel {
    const message = createBaseUiLabel();
    message.id = object.id ?? "";
    message.text = object.text ?? "";
    message.warningText = object.warningText ?? "";
    return message;
  },
};

function createBaseUiTextField(): UiTextField {
  return { id: "", value: "", placeholder: "", disabled: false, isNumerical: false };
}

export const UiTextField = {
  encode(message: UiTextField, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.value !== "") {
      writer.uint32(18).string(message.value);
    }
    if (message.placeholder !== "") {
      writer.uint32(26).string(message.placeholder);
    }
    if (message.disabled === true) {
      writer.uint32(32).bool(message.disabled);
    }
    if (message.isNumerical === true) {
      writer.uint32(40).bool(message.isNumerical);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UiTextField {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUiTextField();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.value = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.placeholder = reader.string();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.disabled = reader.bool();
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.isNumerical = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UiTextField {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      value: isSet(object.value) ? globalThis.String(object.value) : "",
      placeholder: isSet(object.placeholder) ? globalThis.String(object.placeholder) : "",
      disabled: isSet(object.disabled) ? globalThis.Boolean(object.disabled) : false,
      isNumerical: isSet(object.isNumerical) ? globalThis.Boolean(object.isNumerical) : false,
    };
  },

  toJSON(message: UiTextField): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.value !== "") {
      obj.value = message.value;
    }
    if (message.placeholder !== "") {
      obj.placeholder = message.placeholder;
    }
    if (message.disabled === true) {
      obj.disabled = message.disabled;
    }
    if (message.isNumerical === true) {
      obj.isNumerical = message.isNumerical;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UiTextField>, I>>(base?: I): UiTextField {
    return UiTextField.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UiTextField>, I>>(object: I): UiTextField {
    const message = createBaseUiTextField();
    message.id = object.id ?? "";
    message.value = object.value ?? "";
    message.placeholder = object.placeholder ?? "";
    message.disabled = object.disabled ?? false;
    message.isNumerical = object.isNumerical ?? false;
    return message;
  },
};

function createBaseWorkflowStep(): WorkflowStep {
  return { stepId: "", stepTitle: "", completed: false, isEditable: false, elements: [] };
}

export const WorkflowStep = {
  encode(message: WorkflowStep, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.stepId !== "") {
      writer.uint32(10).string(message.stepId);
    }
    if (message.stepTitle !== "") {
      writer.uint32(18).string(message.stepTitle);
    }
    if (message.completed === true) {
      writer.uint32(24).bool(message.completed);
    }
    if (message.isEditable === true) {
      writer.uint32(32).bool(message.isEditable);
    }
    for (const v of message.elements) {
      WorkflowElement.encode(v!, writer.uint32(42).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): WorkflowStep {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseWorkflowStep();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.stepId = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.stepTitle = reader.string();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.completed = reader.bool();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.isEditable = reader.bool();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.elements.push(WorkflowElement.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): WorkflowStep {
    return {
      stepId: isSet(object.stepId) ? globalThis.String(object.stepId) : "",
      stepTitle: isSet(object.stepTitle) ? globalThis.String(object.stepTitle) : "",
      completed: isSet(object.completed) ? globalThis.Boolean(object.completed) : false,
      isEditable: isSet(object.isEditable) ? globalThis.Boolean(object.isEditable) : false,
      elements: globalThis.Array.isArray(object?.elements)
        ? object.elements.map((e: any) => WorkflowElement.fromJSON(e))
        : [],
    };
  },

  toJSON(message: WorkflowStep): unknown {
    const obj: any = {};
    if (message.stepId !== "") {
      obj.stepId = message.stepId;
    }
    if (message.stepTitle !== "") {
      obj.stepTitle = message.stepTitle;
    }
    if (message.completed === true) {
      obj.completed = message.completed;
    }
    if (message.isEditable === true) {
      obj.isEditable = message.isEditable;
    }
    if (message.elements?.length) {
      obj.elements = message.elements.map((e) => WorkflowElement.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<WorkflowStep>, I>>(base?: I): WorkflowStep {
    return WorkflowStep.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<WorkflowStep>, I>>(object: I): WorkflowStep {
    const message = createBaseWorkflowStep();
    message.stepId = object.stepId ?? "";
    message.stepTitle = object.stepTitle ?? "";
    message.completed = object.completed ?? false;
    message.isEditable = object.isEditable ?? false;
    message.elements = object.elements?.map((e) => WorkflowElement.fromPartial(e)) || [];
    return message;
  },
};

function createBaseUiStat(): UiStat {
  return { label: "", value: "", style: 0 };
}

export const UiStat = {
  encode(message: UiStat, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.label !== "") {
      writer.uint32(10).string(message.label);
    }
    if (message.value !== "") {
      writer.uint32(18).string(message.value);
    }
    if (message.style !== 0) {
      writer.uint32(24).int32(message.style);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UiStat {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUiStat();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.label = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.value = reader.string();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.style = reader.int32() as any;
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UiStat {
    return {
      label: isSet(object.label) ? globalThis.String(object.label) : "",
      value: isSet(object.value) ? globalThis.String(object.value) : "",
      style: isSet(object.style) ? attributeStyleFromJSON(object.style) : 0,
    };
  },

  toJSON(message: UiStat): unknown {
    const obj: any = {};
    if (message.label !== "") {
      obj.label = message.label;
    }
    if (message.value !== "") {
      obj.value = message.value;
    }
    if (message.style !== 0) {
      obj.style = attributeStyleToJSON(message.style);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UiStat>, I>>(base?: I): UiStat {
    return UiStat.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UiStat>, I>>(object: I): UiStat {
    const message = createBaseUiStat();
    message.label = object.label ?? "";
    message.value = object.value ?? "";
    message.style = object.style ?? 0;
    return message;
  },
};

function createBaseUiSubmenu(): UiSubmenu {
  return { id: "", pageId: "", textLabel: "", detailLabel: "", showDangerIndicator: false, disabled: false, tag: "" };
}

export const UiSubmenu = {
  encode(message: UiSubmenu, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.pageId !== "") {
      writer.uint32(18).string(message.pageId);
    }
    if (message.textLabel !== "") {
      writer.uint32(26).string(message.textLabel);
    }
    if (message.detailLabel !== "") {
      writer.uint32(34).string(message.detailLabel);
    }
    if (message.showDangerIndicator === true) {
      writer.uint32(40).bool(message.showDangerIndicator);
    }
    if (message.disabled === true) {
      writer.uint32(48).bool(message.disabled);
    }
    if (message.tag !== "") {
      writer.uint32(58).string(message.tag);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UiSubmenu {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUiSubmenu();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.pageId = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.textLabel = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.detailLabel = reader.string();
          continue;
        case 5:
          if (tag !== 40) {
            break;
          }

          message.showDangerIndicator = reader.bool();
          continue;
        case 6:
          if (tag !== 48) {
            break;
          }

          message.disabled = reader.bool();
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.tag = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UiSubmenu {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      pageId: isSet(object.pageId) ? globalThis.String(object.pageId) : "",
      textLabel: isSet(object.textLabel) ? globalThis.String(object.textLabel) : "",
      detailLabel: isSet(object.detailLabel) ? globalThis.String(object.detailLabel) : "",
      showDangerIndicator: isSet(object.showDangerIndicator) ? globalThis.Boolean(object.showDangerIndicator) : false,
      disabled: isSet(object.disabled) ? globalThis.Boolean(object.disabled) : false,
      tag: isSet(object.tag) ? globalThis.String(object.tag) : "",
    };
  },

  toJSON(message: UiSubmenu): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.pageId !== "") {
      obj.pageId = message.pageId;
    }
    if (message.textLabel !== "") {
      obj.textLabel = message.textLabel;
    }
    if (message.detailLabel !== "") {
      obj.detailLabel = message.detailLabel;
    }
    if (message.showDangerIndicator === true) {
      obj.showDangerIndicator = message.showDangerIndicator;
    }
    if (message.disabled === true) {
      obj.disabled = message.disabled;
    }
    if (message.tag !== "") {
      obj.tag = message.tag;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UiSubmenu>, I>>(base?: I): UiSubmenu {
    return UiSubmenu.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UiSubmenu>, I>>(object: I): UiSubmenu {
    const message = createBaseUiSubmenu();
    message.id = object.id ?? "";
    message.pageId = object.pageId ?? "";
    message.textLabel = object.textLabel ?? "";
    message.detailLabel = object.detailLabel ?? "";
    message.showDangerIndicator = object.showDangerIndicator ?? false;
    message.disabled = object.disabled ?? false;
    message.tag = object.tag ?? "";
    return message;
  },
};

function createBaseWorkflowMenuDisplayStateRequest(): WorkflowMenuDisplayStateRequest {
  return { state: 0, preference: 0 };
}

export const WorkflowMenuDisplayStateRequest = {
  encode(message: WorkflowMenuDisplayStateRequest, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.state !== 0) {
      writer.uint32(8).int32(message.state);
    }
    if (message.preference !== 0) {
      writer.uint32(16).int32(message.preference);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): WorkflowMenuDisplayStateRequest {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseWorkflowMenuDisplayStateRequest();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.state = reader.int32() as any;
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.preference = reader.int32() as any;
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): WorkflowMenuDisplayStateRequest {
    return {
      state: isSet(object.state) ? workflowMenuDisplayStateFromJSON(object.state) : 0,
      preference: isSet(object.preference) ? workflowMenuDisplayPreferenceFromJSON(object.preference) : 0,
    };
  },

  toJSON(message: WorkflowMenuDisplayStateRequest): unknown {
    const obj: any = {};
    if (message.state !== 0) {
      obj.state = workflowMenuDisplayStateToJSON(message.state);
    }
    if (message.preference !== 0) {
      obj.preference = workflowMenuDisplayPreferenceToJSON(message.preference);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<WorkflowMenuDisplayStateRequest>, I>>(base?: I): WorkflowMenuDisplayStateRequest {
    return WorkflowMenuDisplayStateRequest.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<WorkflowMenuDisplayStateRequest>, I>>(
    object: I,
  ): WorkflowMenuDisplayStateRequest {
    const message = createBaseWorkflowMenuDisplayStateRequest();
    message.state = object.state ?? 0;
    message.preference = object.preference ?? 0;
    return message;
  },
};

function createBaseWorkflowLayoutAck(): WorkflowLayoutAck {
  return { utime: 0, nonce: 0 };
}

export const WorkflowLayoutAck = {
  encode(message: WorkflowLayoutAck, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).int64(message.utime);
    }
    if (message.nonce !== 0) {
      writer.uint32(16).int64(message.nonce);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): WorkflowLayoutAck {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseWorkflowLayoutAck();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.int64() as Long);
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.nonce = longToNumber(reader.int64() as Long);
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): WorkflowLayoutAck {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      nonce: isSet(object.nonce) ? globalThis.Number(object.nonce) : 0,
    };
  },

  toJSON(message: WorkflowLayoutAck): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.nonce !== 0) {
      obj.nonce = Math.round(message.nonce);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<WorkflowLayoutAck>, I>>(base?: I): WorkflowLayoutAck {
    return WorkflowLayoutAck.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<WorkflowLayoutAck>, I>>(object: I): WorkflowLayoutAck {
    const message = createBaseWorkflowLayoutAck();
    message.utime = object.utime ?? 0;
    message.nonce = object.nonce ?? 0;
    return message;
  },
};

function createBaseWorkflowClientState(): WorkflowClientState {
  return {
    utime: 0,
    nonce: 0,
    primaryStepId: "",
    secondaryStepId: "",
    lastUpdateUtime: 0,
    pageStack: [],
    displayState: 0,
  };
}

export const WorkflowClientState = {
  encode(message: WorkflowClientState, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).uint64(message.utime);
    }
    if (message.nonce !== 0) {
      writer.uint32(16).int64(message.nonce);
    }
    if (message.primaryStepId !== "") {
      writer.uint32(26).string(message.primaryStepId);
    }
    if (message.secondaryStepId !== "") {
      writer.uint32(58).string(message.secondaryStepId);
    }
    if (message.lastUpdateUtime !== 0) {
      writer.uint32(32).uint64(message.lastUpdateUtime);
    }
    for (const v of message.pageStack) {
      writer.uint32(42).string(v!);
    }
    if (message.displayState !== 0) {
      writer.uint32(48).int32(message.displayState);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): WorkflowClientState {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseWorkflowClientState();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.uint64() as Long);
          continue;
        case 2:
          if (tag !== 16) {
            break;
          }

          message.nonce = longToNumber(reader.int64() as Long);
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.primaryStepId = reader.string();
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.secondaryStepId = reader.string();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.lastUpdateUtime = longToNumber(reader.uint64() as Long);
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.pageStack.push(reader.string());
          continue;
        case 6:
          if (tag !== 48) {
            break;
          }

          message.displayState = reader.int32() as any;
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): WorkflowClientState {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      nonce: isSet(object.nonce) ? globalThis.Number(object.nonce) : 0,
      primaryStepId: isSet(object.primaryStepId) ? globalThis.String(object.primaryStepId) : "",
      secondaryStepId: isSet(object.secondaryStepId) ? globalThis.String(object.secondaryStepId) : "",
      lastUpdateUtime: isSet(object.lastUpdateUtime) ? globalThis.Number(object.lastUpdateUtime) : 0,
      pageStack: globalThis.Array.isArray(object?.pageStack)
        ? object.pageStack.map((e: any) => globalThis.String(e))
        : [],
      displayState: isSet(object.displayState) ? workflowMenuDisplayStateFromJSON(object.displayState) : 0,
    };
  },

  toJSON(message: WorkflowClientState): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.nonce !== 0) {
      obj.nonce = Math.round(message.nonce);
    }
    if (message.primaryStepId !== "") {
      obj.primaryStepId = message.primaryStepId;
    }
    if (message.secondaryStepId !== "") {
      obj.secondaryStepId = message.secondaryStepId;
    }
    if (message.lastUpdateUtime !== 0) {
      obj.lastUpdateUtime = Math.round(message.lastUpdateUtime);
    }
    if (message.pageStack?.length) {
      obj.pageStack = message.pageStack;
    }
    if (message.displayState !== 0) {
      obj.displayState = workflowMenuDisplayStateToJSON(message.displayState);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<WorkflowClientState>, I>>(base?: I): WorkflowClientState {
    return WorkflowClientState.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<WorkflowClientState>, I>>(object: I): WorkflowClientState {
    const message = createBaseWorkflowClientState();
    message.utime = object.utime ?? 0;
    message.nonce = object.nonce ?? 0;
    message.primaryStepId = object.primaryStepId ?? "";
    message.secondaryStepId = object.secondaryStepId ?? "";
    message.lastUpdateUtime = object.lastUpdateUtime ?? 0;
    message.pageStack = object.pageStack?.map((e) => e) || [];
    message.displayState = object.displayState ?? 0;
    return message;
  },
};

function createBaseUiStatsView(): UiStatsView {
  return { id: "", stats: [] };
}

export const UiStatsView = {
  encode(message: UiStatsView, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(18).string(message.id);
    }
    for (const v of message.stats) {
      UiStat.encode(v!, writer.uint32(10).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UiStatsView {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUiStatsView();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 2:
          if (tag !== 18) {
            break;
          }

          message.id = reader.string();
          continue;
        case 1:
          if (tag !== 10) {
            break;
          }

          message.stats.push(UiStat.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UiStatsView {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      stats: globalThis.Array.isArray(object?.stats) ? object.stats.map((e: any) => UiStat.fromJSON(e)) : [],
    };
  },

  toJSON(message: UiStatsView): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.stats?.length) {
      obj.stats = message.stats.map((e) => UiStat.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UiStatsView>, I>>(base?: I): UiStatsView {
    return UiStatsView.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UiStatsView>, I>>(object: I): UiStatsView {
    const message = createBaseUiStatsView();
    message.id = object.id ?? "";
    message.stats = object.stats?.map((e) => UiStat.fromPartial(e)) || [];
    return message;
  },
};

function createBaseWorkflowHUD(): WorkflowHUD {
  return {
    hudButtons: [],
    statistics: undefined,
    photoCapture: undefined,
    toggleCoverageMesh: undefined,
    viewpointSelection: undefined,
    rthButton: undefined,
    mapToggleButton: undefined,
    enableHwButtons: false,
    prompts: [],
  };
}

export const WorkflowHUD = {
  encode(message: WorkflowHUD, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    for (const v of message.hudButtons) {
      UiButton.encode(v!, writer.uint32(10).fork()).ldelim();
    }
    if (message.statistics !== undefined) {
      UiStatsView.encode(message.statistics, writer.uint32(18).fork()).ldelim();
    }
    if (message.photoCapture !== undefined) {
      UiButton.encode(message.photoCapture, writer.uint32(26).fork()).ldelim();
    }
    if (message.toggleCoverageMesh !== undefined) {
      UiButton.encode(message.toggleCoverageMesh, writer.uint32(34).fork()).ldelim();
    }
    if (message.viewpointSelection !== undefined) {
      UiButton.encode(message.viewpointSelection, writer.uint32(42).fork()).ldelim();
    }
    if (message.rthButton !== undefined) {
      UiButton.encode(message.rthButton, writer.uint32(50).fork()).ldelim();
    }
    if (message.mapToggleButton !== undefined) {
      UiButton.encode(message.mapToggleButton, writer.uint32(58).fork()).ldelim();
    }
    if (message.enableHwButtons === true) {
      writer.uint32(64).bool(message.enableHwButtons);
    }
    for (const v of message.prompts) {
      UiPrompt.encode(v!, writer.uint32(74).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): WorkflowHUD {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseWorkflowHUD();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.hudButtons.push(UiButton.decode(reader, reader.uint32()));
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.statistics = UiStatsView.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.photoCapture = UiButton.decode(reader, reader.uint32());
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.toggleCoverageMesh = UiButton.decode(reader, reader.uint32());
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.viewpointSelection = UiButton.decode(reader, reader.uint32());
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.rthButton = UiButton.decode(reader, reader.uint32());
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.mapToggleButton = UiButton.decode(reader, reader.uint32());
          continue;
        case 8:
          if (tag !== 64) {
            break;
          }

          message.enableHwButtons = reader.bool();
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.prompts.push(UiPrompt.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): WorkflowHUD {
    return {
      hudButtons: globalThis.Array.isArray(object?.hudButtons)
        ? object.hudButtons.map((e: any) => UiButton.fromJSON(e))
        : [],
      statistics: isSet(object.statistics) ? UiStatsView.fromJSON(object.statistics) : undefined,
      photoCapture: isSet(object.photoCapture) ? UiButton.fromJSON(object.photoCapture) : undefined,
      toggleCoverageMesh: isSet(object.toggleCoverageMesh) ? UiButton.fromJSON(object.toggleCoverageMesh) : undefined,
      viewpointSelection: isSet(object.viewpointSelection) ? UiButton.fromJSON(object.viewpointSelection) : undefined,
      rthButton: isSet(object.rthButton) ? UiButton.fromJSON(object.rthButton) : undefined,
      mapToggleButton: isSet(object.mapToggleButton) ? UiButton.fromJSON(object.mapToggleButton) : undefined,
      enableHwButtons: isSet(object.enableHwButtons) ? globalThis.Boolean(object.enableHwButtons) : false,
      prompts: globalThis.Array.isArray(object?.prompts) ? object.prompts.map((e: any) => UiPrompt.fromJSON(e)) : [],
    };
  },

  toJSON(message: WorkflowHUD): unknown {
    const obj: any = {};
    if (message.hudButtons?.length) {
      obj.hudButtons = message.hudButtons.map((e) => UiButton.toJSON(e));
    }
    if (message.statistics !== undefined) {
      obj.statistics = UiStatsView.toJSON(message.statistics);
    }
    if (message.photoCapture !== undefined) {
      obj.photoCapture = UiButton.toJSON(message.photoCapture);
    }
    if (message.toggleCoverageMesh !== undefined) {
      obj.toggleCoverageMesh = UiButton.toJSON(message.toggleCoverageMesh);
    }
    if (message.viewpointSelection !== undefined) {
      obj.viewpointSelection = UiButton.toJSON(message.viewpointSelection);
    }
    if (message.rthButton !== undefined) {
      obj.rthButton = UiButton.toJSON(message.rthButton);
    }
    if (message.mapToggleButton !== undefined) {
      obj.mapToggleButton = UiButton.toJSON(message.mapToggleButton);
    }
    if (message.enableHwButtons === true) {
      obj.enableHwButtons = message.enableHwButtons;
    }
    if (message.prompts?.length) {
      obj.prompts = message.prompts.map((e) => UiPrompt.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<WorkflowHUD>, I>>(base?: I): WorkflowHUD {
    return WorkflowHUD.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<WorkflowHUD>, I>>(object: I): WorkflowHUD {
    const message = createBaseWorkflowHUD();
    message.hudButtons = object.hudButtons?.map((e) => UiButton.fromPartial(e)) || [];
    message.statistics = (object.statistics !== undefined && object.statistics !== null)
      ? UiStatsView.fromPartial(object.statistics)
      : undefined;
    message.photoCapture = (object.photoCapture !== undefined && object.photoCapture !== null)
      ? UiButton.fromPartial(object.photoCapture)
      : undefined;
    message.toggleCoverageMesh = (object.toggleCoverageMesh !== undefined && object.toggleCoverageMesh !== null)
      ? UiButton.fromPartial(object.toggleCoverageMesh)
      : undefined;
    message.viewpointSelection = (object.viewpointSelection !== undefined && object.viewpointSelection !== null)
      ? UiButton.fromPartial(object.viewpointSelection)
      : undefined;
    message.rthButton = (object.rthButton !== undefined && object.rthButton !== null)
      ? UiButton.fromPartial(object.rthButton)
      : undefined;
    message.mapToggleButton = (object.mapToggleButton !== undefined && object.mapToggleButton !== null)
      ? UiButton.fromPartial(object.mapToggleButton)
      : undefined;
    message.enableHwButtons = object.enableHwButtons ?? false;
    message.prompts = object.prompts?.map((e) => UiPrompt.fromPartial(e)) || [];
    return message;
  },
};

function createBaseWorkflowSettingsPage(): WorkflowSettingsPage {
  return { pageId: "", pageTitle: "", elements: [] };
}

export const WorkflowSettingsPage = {
  encode(message: WorkflowSettingsPage, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.pageId !== "") {
      writer.uint32(10).string(message.pageId);
    }
    if (message.pageTitle !== "") {
      writer.uint32(18).string(message.pageTitle);
    }
    for (const v of message.elements) {
      WorkflowElement.encode(v!, writer.uint32(26).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): WorkflowSettingsPage {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseWorkflowSettingsPage();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.pageId = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.pageTitle = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.elements.push(WorkflowElement.decode(reader, reader.uint32()));
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): WorkflowSettingsPage {
    return {
      pageId: isSet(object.pageId) ? globalThis.String(object.pageId) : "",
      pageTitle: isSet(object.pageTitle) ? globalThis.String(object.pageTitle) : "",
      elements: globalThis.Array.isArray(object?.elements)
        ? object.elements.map((e: any) => WorkflowElement.fromJSON(e))
        : [],
    };
  },

  toJSON(message: WorkflowSettingsPage): unknown {
    const obj: any = {};
    if (message.pageId !== "") {
      obj.pageId = message.pageId;
    }
    if (message.pageTitle !== "") {
      obj.pageTitle = message.pageTitle;
    }
    if (message.elements?.length) {
      obj.elements = message.elements.map((e) => WorkflowElement.toJSON(e));
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<WorkflowSettingsPage>, I>>(base?: I): WorkflowSettingsPage {
    return WorkflowSettingsPage.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<WorkflowSettingsPage>, I>>(object: I): WorkflowSettingsPage {
    const message = createBaseWorkflowSettingsPage();
    message.pageId = object.pageId ?? "";
    message.pageTitle = object.pageTitle ?? "";
    message.elements = object.elements?.map((e) => WorkflowElement.fromPartial(e)) || [];
    return message;
  },
};

function createBaseWorkflowScreen(): WorkflowScreen {
  return { basic: undefined, postFlight: undefined, exitCockpit: false };
}

export const WorkflowScreen = {
  encode(message: WorkflowScreen, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.basic !== undefined) {
      UiScreenBasic.encode(message.basic, writer.uint32(10).fork()).ldelim();
    }
    if (message.postFlight !== undefined) {
      UiScreenPostFlight.encode(message.postFlight, writer.uint32(18).fork()).ldelim();
    }
    if (message.exitCockpit === true) {
      writer.uint32(24).bool(message.exitCockpit);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): WorkflowScreen {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseWorkflowScreen();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.basic = UiScreenBasic.decode(reader, reader.uint32());
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.postFlight = UiScreenPostFlight.decode(reader, reader.uint32());
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.exitCockpit = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): WorkflowScreen {
    return {
      basic: isSet(object.basic) ? UiScreenBasic.fromJSON(object.basic) : undefined,
      postFlight: isSet(object.postFlight) ? UiScreenPostFlight.fromJSON(object.postFlight) : undefined,
      exitCockpit: isSet(object.exitCockpit) ? globalThis.Boolean(object.exitCockpit) : false,
    };
  },

  toJSON(message: WorkflowScreen): unknown {
    const obj: any = {};
    if (message.basic !== undefined) {
      obj.basic = UiScreenBasic.toJSON(message.basic);
    }
    if (message.postFlight !== undefined) {
      obj.postFlight = UiScreenPostFlight.toJSON(message.postFlight);
    }
    if (message.exitCockpit === true) {
      obj.exitCockpit = message.exitCockpit;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<WorkflowScreen>, I>>(base?: I): WorkflowScreen {
    return WorkflowScreen.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<WorkflowScreen>, I>>(object: I): WorkflowScreen {
    const message = createBaseWorkflowScreen();
    message.basic = (object.basic !== undefined && object.basic !== null)
      ? UiScreenBasic.fromPartial(object.basic)
      : undefined;
    message.postFlight = (object.postFlight !== undefined && object.postFlight !== null)
      ? UiScreenPostFlight.fromPartial(object.postFlight)
      : undefined;
    message.exitCockpit = object.exitCockpit ?? false;
    return message;
  },
};

function createBaseUiScreenPostFlight(): UiScreenPostFlight {
  return {
    id: "",
    title: "",
    imagePath: "",
    heading: "",
    subheading: "",
    stats: undefined,
    buttons: [],
    checkbox: undefined,
  };
}

export const UiScreenPostFlight = {
  encode(message: UiScreenPostFlight, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.title !== "") {
      writer.uint32(18).string(message.title);
    }
    if (message.imagePath !== "") {
      writer.uint32(26).string(message.imagePath);
    }
    if (message.heading !== "") {
      writer.uint32(34).string(message.heading);
    }
    if (message.subheading !== "") {
      writer.uint32(42).string(message.subheading);
    }
    if (message.stats !== undefined) {
      UiStatsView.encode(message.stats, writer.uint32(50).fork()).ldelim();
    }
    for (const v of message.buttons) {
      UiButton.encode(v!, writer.uint32(58).fork()).ldelim();
    }
    if (message.checkbox !== undefined) {
      UiCheckbox.encode(message.checkbox, writer.uint32(66).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UiScreenPostFlight {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUiScreenPostFlight();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.title = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.imagePath = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.heading = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.subheading = reader.string();
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.stats = UiStatsView.decode(reader, reader.uint32());
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.buttons.push(UiButton.decode(reader, reader.uint32()));
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.checkbox = UiCheckbox.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UiScreenPostFlight {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      title: isSet(object.title) ? globalThis.String(object.title) : "",
      imagePath: isSet(object.imagePath) ? globalThis.String(object.imagePath) : "",
      heading: isSet(object.heading) ? globalThis.String(object.heading) : "",
      subheading: isSet(object.subheading) ? globalThis.String(object.subheading) : "",
      stats: isSet(object.stats) ? UiStatsView.fromJSON(object.stats) : undefined,
      buttons: globalThis.Array.isArray(object?.buttons) ? object.buttons.map((e: any) => UiButton.fromJSON(e)) : [],
      checkbox: isSet(object.checkbox) ? UiCheckbox.fromJSON(object.checkbox) : undefined,
    };
  },

  toJSON(message: UiScreenPostFlight): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.title !== "") {
      obj.title = message.title;
    }
    if (message.imagePath !== "") {
      obj.imagePath = message.imagePath;
    }
    if (message.heading !== "") {
      obj.heading = message.heading;
    }
    if (message.subheading !== "") {
      obj.subheading = message.subheading;
    }
    if (message.stats !== undefined) {
      obj.stats = UiStatsView.toJSON(message.stats);
    }
    if (message.buttons?.length) {
      obj.buttons = message.buttons.map((e) => UiButton.toJSON(e));
    }
    if (message.checkbox !== undefined) {
      obj.checkbox = UiCheckbox.toJSON(message.checkbox);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UiScreenPostFlight>, I>>(base?: I): UiScreenPostFlight {
    return UiScreenPostFlight.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UiScreenPostFlight>, I>>(object: I): UiScreenPostFlight {
    const message = createBaseUiScreenPostFlight();
    message.id = object.id ?? "";
    message.title = object.title ?? "";
    message.imagePath = object.imagePath ?? "";
    message.heading = object.heading ?? "";
    message.subheading = object.subheading ?? "";
    message.stats = (object.stats !== undefined && object.stats !== null)
      ? UiStatsView.fromPartial(object.stats)
      : undefined;
    message.buttons = object.buttons?.map((e) => UiButton.fromPartial(e)) || [];
    message.checkbox = (object.checkbox !== undefined && object.checkbox !== null)
      ? UiCheckbox.fromPartial(object.checkbox)
      : undefined;
    return message;
  },
};

function createBaseUiScreenBasic(): UiScreenBasic {
  return {
    id: "",
    imageId: "",
    iconType: 0,
    title: "",
    subtitle: "",
    buttons: [],
    checkbox: undefined,
    progressRatio: 0,
    leftPaneImageId: "",
    scanId: "",
  };
}

export const UiScreenBasic = {
  encode(message: UiScreenBasic, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.imageId !== "") {
      writer.uint32(18).string(message.imageId);
    }
    if (message.iconType !== 0) {
      writer.uint32(80).int32(message.iconType);
    }
    if (message.title !== "") {
      writer.uint32(26).string(message.title);
    }
    if (message.subtitle !== "") {
      writer.uint32(34).string(message.subtitle);
    }
    for (const v of message.buttons) {
      UiButton.encode(v!, writer.uint32(42).fork()).ldelim();
    }
    if (message.checkbox !== undefined) {
      UiCheckbox.encode(message.checkbox, writer.uint32(50).fork()).ldelim();
    }
    if (message.progressRatio !== 0) {
      writer.uint32(61).float(message.progressRatio);
    }
    if (message.leftPaneImageId !== "") {
      writer.uint32(66).string(message.leftPaneImageId);
    }
    if (message.scanId !== "") {
      writer.uint32(74).string(message.scanId);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UiScreenBasic {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUiScreenBasic();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.imageId = reader.string();
          continue;
        case 10:
          if (tag !== 80) {
            break;
          }

          message.iconType = reader.int32() as any;
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.title = reader.string();
          continue;
        case 4:
          if (tag !== 34) {
            break;
          }

          message.subtitle = reader.string();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.buttons.push(UiButton.decode(reader, reader.uint32()));
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.checkbox = UiCheckbox.decode(reader, reader.uint32());
          continue;
        case 7:
          if (tag !== 61) {
            break;
          }

          message.progressRatio = reader.float();
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.leftPaneImageId = reader.string();
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.scanId = reader.string();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UiScreenBasic {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      imageId: isSet(object.imageId) ? globalThis.String(object.imageId) : "",
      iconType: isSet(object.iconType) ? workflowIconTypeFromJSON(object.iconType) : 0,
      title: isSet(object.title) ? globalThis.String(object.title) : "",
      subtitle: isSet(object.subtitle) ? globalThis.String(object.subtitle) : "",
      buttons: globalThis.Array.isArray(object?.buttons) ? object.buttons.map((e: any) => UiButton.fromJSON(e)) : [],
      checkbox: isSet(object.checkbox) ? UiCheckbox.fromJSON(object.checkbox) : undefined,
      progressRatio: isSet(object.progressRatio) ? globalThis.Number(object.progressRatio) : 0,
      leftPaneImageId: isSet(object.leftPaneImageId) ? globalThis.String(object.leftPaneImageId) : "",
      scanId: isSet(object.scanId) ? globalThis.String(object.scanId) : "",
    };
  },

  toJSON(message: UiScreenBasic): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.imageId !== "") {
      obj.imageId = message.imageId;
    }
    if (message.iconType !== 0) {
      obj.iconType = workflowIconTypeToJSON(message.iconType);
    }
    if (message.title !== "") {
      obj.title = message.title;
    }
    if (message.subtitle !== "") {
      obj.subtitle = message.subtitle;
    }
    if (message.buttons?.length) {
      obj.buttons = message.buttons.map((e) => UiButton.toJSON(e));
    }
    if (message.checkbox !== undefined) {
      obj.checkbox = UiCheckbox.toJSON(message.checkbox);
    }
    if (message.progressRatio !== 0) {
      obj.progressRatio = message.progressRatio;
    }
    if (message.leftPaneImageId !== "") {
      obj.leftPaneImageId = message.leftPaneImageId;
    }
    if (message.scanId !== "") {
      obj.scanId = message.scanId;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UiScreenBasic>, I>>(base?: I): UiScreenBasic {
    return UiScreenBasic.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UiScreenBasic>, I>>(object: I): UiScreenBasic {
    const message = createBaseUiScreenBasic();
    message.id = object.id ?? "";
    message.imageId = object.imageId ?? "";
    message.iconType = object.iconType ?? 0;
    message.title = object.title ?? "";
    message.subtitle = object.subtitle ?? "";
    message.buttons = object.buttons?.map((e) => UiButton.fromPartial(e)) || [];
    message.checkbox = (object.checkbox !== undefined && object.checkbox !== null)
      ? UiCheckbox.fromPartial(object.checkbox)
      : undefined;
    message.progressRatio = object.progressRatio ?? 0;
    message.leftPaneImageId = object.leftPaneImageId ?? "";
    message.scanId = object.scanId ?? "";
    return message;
  },
};

function createBaseUiCheckbox(): UiCheckbox {
  return { id: "", text: "", value: false };
}

export const UiCheckbox = {
  encode(message: UiCheckbox, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.text !== "") {
      writer.uint32(18).string(message.text);
    }
    if (message.value === true) {
      writer.uint32(24).bool(message.value);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UiCheckbox {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUiCheckbox();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.text = reader.string();
          continue;
        case 3:
          if (tag !== 24) {
            break;
          }

          message.value = reader.bool();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UiCheckbox {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      text: isSet(object.text) ? globalThis.String(object.text) : "",
      value: isSet(object.value) ? globalThis.Boolean(object.value) : false,
    };
  },

  toJSON(message: UiCheckbox): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.text !== "") {
      obj.text = message.text;
    }
    if (message.value === true) {
      obj.value = message.value;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UiCheckbox>, I>>(base?: I): UiCheckbox {
    return UiCheckbox.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UiCheckbox>, I>>(object: I): UiCheckbox {
    const message = createBaseUiCheckbox();
    message.id = object.id ?? "";
    message.text = object.text ?? "";
    message.value = object.value ?? false;
    return message;
  },
};

function createBaseUiProgressBar(): UiProgressBar {
  return { id: "", progressRatio: 0 };
}

export const UiProgressBar = {
  encode(message: UiProgressBar, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.id !== "") {
      writer.uint32(10).string(message.id);
    }
    if (message.progressRatio !== 0) {
      writer.uint32(21).float(message.progressRatio);
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): UiProgressBar {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseUiProgressBar();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 10) {
            break;
          }

          message.id = reader.string();
          continue;
        case 2:
          if (tag !== 21) {
            break;
          }

          message.progressRatio = reader.float();
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): UiProgressBar {
    return {
      id: isSet(object.id) ? globalThis.String(object.id) : "",
      progressRatio: isSet(object.progressRatio) ? globalThis.Number(object.progressRatio) : 0,
    };
  },

  toJSON(message: UiProgressBar): unknown {
    const obj: any = {};
    if (message.id !== "") {
      obj.id = message.id;
    }
    if (message.progressRatio !== 0) {
      obj.progressRatio = message.progressRatio;
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<UiProgressBar>, I>>(base?: I): UiProgressBar {
    return UiProgressBar.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<UiProgressBar>, I>>(object: I): UiProgressBar {
    const message = createBaseUiProgressBar();
    message.id = object.id ?? "";
    message.progressRatio = object.progressRatio ?? 0;
    return message;
  },
};

function createBaseWorkflowLayout(): WorkflowLayout {
  return {
    utime: 0,
    nonce: 0,
    statusTitle: "",
    statusDetail: "",
    currentStep: 0,
    steps: [],
    pages: [],
    screen: undefined,
    request: undefined,
    hud: undefined,
    headerAccessoryButton: undefined,
  };
}

export const WorkflowLayout = {
  encode(message: WorkflowLayout, writer: _m0.Writer = _m0.Writer.create()): _m0.Writer {
    if (message.utime !== 0) {
      writer.uint32(8).int64(message.utime);
    }
    if (message.nonce !== 0) {
      writer.uint32(88).int64(message.nonce);
    }
    if (message.statusTitle !== "") {
      writer.uint32(18).string(message.statusTitle);
    }
    if (message.statusDetail !== "") {
      writer.uint32(26).string(message.statusDetail);
    }
    if (message.currentStep !== 0) {
      writer.uint32(32).int32(message.currentStep);
    }
    for (const v of message.steps) {
      WorkflowStep.encode(v!, writer.uint32(42).fork()).ldelim();
    }
    for (const v of message.pages) {
      WorkflowSettingsPage.encode(v!, writer.uint32(50).fork()).ldelim();
    }
    if (message.screen !== undefined) {
      WorkflowScreen.encode(message.screen, writer.uint32(58).fork()).ldelim();
    }
    if (message.request !== undefined) {
      WorkflowMenuDisplayStateRequest.encode(message.request, writer.uint32(66).fork()).ldelim();
    }
    if (message.hud !== undefined) {
      WorkflowHUD.encode(message.hud, writer.uint32(74).fork()).ldelim();
    }
    if (message.headerAccessoryButton !== undefined) {
      UiButton.encode(message.headerAccessoryButton, writer.uint32(82).fork()).ldelim();
    }
    return writer;
  },

  decode(input: _m0.Reader | Uint8Array, length?: number): WorkflowLayout {
    const reader = input instanceof _m0.Reader ? input : _m0.Reader.create(input);
    let end = length === undefined ? reader.len : reader.pos + length;
    const message = createBaseWorkflowLayout();
    while (reader.pos < end) {
      const tag = reader.uint32();
      switch (tag >>> 3) {
        case 1:
          if (tag !== 8) {
            break;
          }

          message.utime = longToNumber(reader.int64() as Long);
          continue;
        case 11:
          if (tag !== 88) {
            break;
          }

          message.nonce = longToNumber(reader.int64() as Long);
          continue;
        case 2:
          if (tag !== 18) {
            break;
          }

          message.statusTitle = reader.string();
          continue;
        case 3:
          if (tag !== 26) {
            break;
          }

          message.statusDetail = reader.string();
          continue;
        case 4:
          if (tag !== 32) {
            break;
          }

          message.currentStep = reader.int32();
          continue;
        case 5:
          if (tag !== 42) {
            break;
          }

          message.steps.push(WorkflowStep.decode(reader, reader.uint32()));
          continue;
        case 6:
          if (tag !== 50) {
            break;
          }

          message.pages.push(WorkflowSettingsPage.decode(reader, reader.uint32()));
          continue;
        case 7:
          if (tag !== 58) {
            break;
          }

          message.screen = WorkflowScreen.decode(reader, reader.uint32());
          continue;
        case 8:
          if (tag !== 66) {
            break;
          }

          message.request = WorkflowMenuDisplayStateRequest.decode(reader, reader.uint32());
          continue;
        case 9:
          if (tag !== 74) {
            break;
          }

          message.hud = WorkflowHUD.decode(reader, reader.uint32());
          continue;
        case 10:
          if (tag !== 82) {
            break;
          }

          message.headerAccessoryButton = UiButton.decode(reader, reader.uint32());
          continue;
      }
      if ((tag & 7) === 4 || tag === 0) {
        break;
      }
      reader.skipType(tag & 7);
    }
    return message;
  },

  fromJSON(object: any): WorkflowLayout {
    return {
      utime: isSet(object.utime) ? globalThis.Number(object.utime) : 0,
      nonce: isSet(object.nonce) ? globalThis.Number(object.nonce) : 0,
      statusTitle: isSet(object.statusTitle) ? globalThis.String(object.statusTitle) : "",
      statusDetail: isSet(object.statusDetail) ? globalThis.String(object.statusDetail) : "",
      currentStep: isSet(object.currentStep) ? globalThis.Number(object.currentStep) : 0,
      steps: globalThis.Array.isArray(object?.steps) ? object.steps.map((e: any) => WorkflowStep.fromJSON(e)) : [],
      pages: globalThis.Array.isArray(object?.pages)
        ? object.pages.map((e: any) => WorkflowSettingsPage.fromJSON(e))
        : [],
      screen: isSet(object.screen) ? WorkflowScreen.fromJSON(object.screen) : undefined,
      request: isSet(object.request) ? WorkflowMenuDisplayStateRequest.fromJSON(object.request) : undefined,
      hud: isSet(object.hud) ? WorkflowHUD.fromJSON(object.hud) : undefined,
      headerAccessoryButton: isSet(object.headerAccessoryButton)
        ? UiButton.fromJSON(object.headerAccessoryButton)
        : undefined,
    };
  },

  toJSON(message: WorkflowLayout): unknown {
    const obj: any = {};
    if (message.utime !== 0) {
      obj.utime = Math.round(message.utime);
    }
    if (message.nonce !== 0) {
      obj.nonce = Math.round(message.nonce);
    }
    if (message.statusTitle !== "") {
      obj.statusTitle = message.statusTitle;
    }
    if (message.statusDetail !== "") {
      obj.statusDetail = message.statusDetail;
    }
    if (message.currentStep !== 0) {
      obj.currentStep = Math.round(message.currentStep);
    }
    if (message.steps?.length) {
      obj.steps = message.steps.map((e) => WorkflowStep.toJSON(e));
    }
    if (message.pages?.length) {
      obj.pages = message.pages.map((e) => WorkflowSettingsPage.toJSON(e));
    }
    if (message.screen !== undefined) {
      obj.screen = WorkflowScreen.toJSON(message.screen);
    }
    if (message.request !== undefined) {
      obj.request = WorkflowMenuDisplayStateRequest.toJSON(message.request);
    }
    if (message.hud !== undefined) {
      obj.hud = WorkflowHUD.toJSON(message.hud);
    }
    if (message.headerAccessoryButton !== undefined) {
      obj.headerAccessoryButton = UiButton.toJSON(message.headerAccessoryButton);
    }
    return obj;
  },

  create<I extends Exact<DeepPartial<WorkflowLayout>, I>>(base?: I): WorkflowLayout {
    return WorkflowLayout.fromPartial(base ?? ({} as any));
  },
  fromPartial<I extends Exact<DeepPartial<WorkflowLayout>, I>>(object: I): WorkflowLayout {
    const message = createBaseWorkflowLayout();
    message.utime = object.utime ?? 0;
    message.nonce = object.nonce ?? 0;
    message.statusTitle = object.statusTitle ?? "";
    message.statusDetail = object.statusDetail ?? "";
    message.currentStep = object.currentStep ?? 0;
    message.steps = object.steps?.map((e) => WorkflowStep.fromPartial(e)) || [];
    message.pages = object.pages?.map((e) => WorkflowSettingsPage.fromPartial(e)) || [];
    message.screen = (object.screen !== undefined && object.screen !== null)
      ? WorkflowScreen.fromPartial(object.screen)
      : undefined;
    message.request = (object.request !== undefined && object.request !== null)
      ? WorkflowMenuDisplayStateRequest.fromPartial(object.request)
      : undefined;
    message.hud = (object.hud !== undefined && object.hud !== null) ? WorkflowHUD.fromPartial(object.hud) : undefined;
    message.headerAccessoryButton =
      (object.headerAccessoryButton !== undefined && object.headerAccessoryButton !== null)
        ? UiButton.fromPartial(object.headerAccessoryButton)
        : undefined;
    return message;
  },
};

type Builtin = Date | Function | Uint8Array | string | number | boolean | undefined;

export type DeepPartial<T> = T extends Builtin ? T
  : T extends globalThis.Array<infer U> ? globalThis.Array<DeepPartial<U>>
  : T extends ReadonlyArray<infer U> ? ReadonlyArray<DeepPartial<U>>
  : T extends {} ? { [K in keyof T]?: DeepPartial<T[K]> }
  : Partial<T>;

type KeysOfUnion<T> = T extends T ? keyof T : never;
export type Exact<P, I extends P> = P extends Builtin ? P
  : P & { [K in keyof P]: Exact<P[K], I[K]> } & { [K in Exclude<keyof I, KeysOfUnion<P>>]: never };

function longToNumber(long: Long): number {
  if (long.gt(globalThis.Number.MAX_SAFE_INTEGER)) {
    throw new globalThis.Error("Value is larger than Number.MAX_SAFE_INTEGER");
  }
  return long.toNumber();
}

if (_m0.util.Long !== Long) {
  _m0.util.Long = Long as any;
  _m0.configure();
}

function isSet(value: any): boolean {
  return value !== null && value !== undefined;
}
